import React, { useState } from 'react'
import {
  IonButton,
  IonIcon,
  IonPopover,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonAlert,
  IonToast,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonInput,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent
} from '@ionic/react'
import { 
  menuOutline, 
  personOutline, 
  logOutOutline, 
  refreshOutline, 
  keyOutline,
  closeOutline 
} from 'ionicons/icons'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'
import { supabase } from '../services/supabase'

const UserMenu: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { user, refreshUser, logout } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)
  const [showLogoutAlert, setShowLogoutAlert] = useState(false)
  const [showRefreshToast, setShowRefreshToast] = useState(false)
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  // Get display name (first name only or email prefix)
  const getDisplayName = () => {
    if (user?.full_name) {
      const firstName = user.full_name.split(' ')[0]
      return firstName
    }
    if (user?.email) {
      return user.email.split('@')[0]
    }
    return 'User'
  }

  const handleProfileClick = () => {
    setIsOpen(false)
    history.push('/app/profile')
  }

  const handleRefreshProfile = async () => {
    setIsOpen(false)
    try {
      await refreshUser()
      setShowRefreshToast(true)
    } catch (error) {
      console.error('Error refreshing profile:', error)
    }
  }

  const handleLogout = async () => {
    setShowLogoutAlert(false)
    setIsOpen(false)
    
    try {
      await supabase.auth.signOut()
      logout()
      history.push('/landing')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const handlePasswordChange = async () => {
    // Reset error
    setPasswordError('')
    
    // Validate passwords
    if (!currentPassword) {
      setPasswordError('Current password is required')
      return
    }
    
    if (!newPassword || newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long')
      return
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }
    
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password')
      return
    }
    
    try {
      // Update password using Supabase Auth
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })
      
      if (error) throw error
      
      setPasswordSuccess(true)
      setShowPasswordModal(false)
      resetPasswordForm()
    } catch (error: any) {
      console.error('Error updating password:', error)
      if (error.message?.includes('Invalid login credentials')) {
        setPasswordError('Current password is incorrect')
      } else {
        setPasswordError('Failed to update password. Please try again.')
      }
    }
  }

  const openPasswordModal = () => {
    setIsOpen(false)
    resetPasswordForm()
    setShowPasswordModal(true)
  }

  const resetPasswordForm = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
  }

  return (
    <>
      <IonButton
        id="user-menu-trigger"
        fill="clear"
        size="small"
        className="text-gray-600 dark:text-gray-300"
      >
        <IonIcon icon={menuOutline} />
      </IonButton>

      <IonPopover
        trigger="user-menu-trigger"
        isOpen={isOpen}
        onDidDismiss={() => setIsOpen(false)}
        onWillPresent={() => setIsOpen(true)}
        showBackdrop={true}
        side="bottom"
        alignment="end"
      >
        <IonContent>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {getDisplayName()}
            </p>
          </div>
          
          <IonList>
            <IonItem button onClick={handleProfileClick}>
              <IonIcon icon={personOutline} slot="start" />
              <IonLabel>{t('menu.profile')}</IonLabel>
            </IonItem>
            
            <IonItem button onClick={openPasswordModal}>
              <IonIcon icon={keyOutline} slot="start" />
              <IonLabel>Change Password</IonLabel>
            </IonItem>
            
            <IonItem button onClick={handleRefreshProfile}>
              <IonIcon icon={refreshOutline} slot="start" />
              <IonLabel>Refresh Profile</IonLabel>
            </IonItem>
            
            <IonItem button onClick={() => setShowLogoutAlert(true)}>
              <IonIcon icon={logOutOutline} slot="start" />
              <IonLabel>{t('menu.logout')}</IonLabel>
            </IonItem>
          </IonList>
        </IonContent>
      </IonPopover>

      <IonAlert
        isOpen={showLogoutAlert}
        onDidDismiss={() => setShowLogoutAlert(false)}
        header={t('menu.confirmLogout')}
        message={t('menu.logoutMessage')}
        buttons={[
          {
            text: t('common.cancel'),
            role: 'cancel'
          },
          {
            text: t('menu.logout'),
            role: 'confirm',
            handler: handleLogout
          }
        ]}
      />

      <IonToast
        isOpen={showRefreshToast}
        onDidDismiss={() => setShowRefreshToast(false)}
        message="Profile refreshed successfully"
        duration={2000}
        position="bottom"
        color="success"
      />

      {/* Password Change Modal */}
      <IonModal isOpen={showPasswordModal} onDidDismiss={() => setShowPasswordModal(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Change Password</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowPasswordModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="space-y-4">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle className="flex items-center text-base sm:text-lg">
                  <IonIcon icon={keyOutline} className="mr-2 flex-shrink-0" />
                  Update Your Password
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="space-y-4">
                  <div>
                    <IonLabel position="stacked">Current Password</IonLabel>
                    <IonInput
                      type="password"
                      value={currentPassword}
                      onIonInput={(e) => setCurrentPassword(e.detail.value!)}
                      placeholder="Enter your current password"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <IonLabel position="stacked">New Password</IonLabel>
                    <IonInput
                      type="password"
                      value={newPassword}
                      onIonInput={(e) => setNewPassword(e.detail.value!)}
                      placeholder="Enter new password (min 6 characters)"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <IonLabel position="stacked">Confirm New Password</IonLabel>
                    <IonInput
                      type="password"
                      value={confirmPassword}
                      onIonInput={(e) => setConfirmPassword(e.detail.value!)}
                      placeholder="Confirm new password"
                      className="mt-1"
                    />
                  </div>
                  
                  {passwordError && (
                    <div className="text-red-500 text-sm mt-2">
                      {passwordError}
                    </div>
                  )}
                  
                  <div className="flex gap-2 mt-6">
                    <IonButton 
                      expand="block" 
                      color="primary"
                      onClick={handlePasswordChange}
                      disabled={!currentPassword || !newPassword || !confirmPassword}
                    >
                      Update Password
                    </IonButton>
                    <IonButton 
                      expand="block" 
                      fill="outline" 
                      color="medium"
                      onClick={() => setShowPasswordModal(false)}
                    >
                      Cancel
                    </IonButton>
                  </div>
                </div>
              </IonCardContent>
            </IonCard>
            
            <IonCard>
              <IonCardContent>
                <div className="text-sm text-gray-600">
                  <p><strong>Security Tips:</strong></p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Use a strong password with at least 6 characters</li>
                    <li>Include a mix of letters, numbers, and symbols</li>
                    <li>Don't reuse passwords from other accounts</li>
                    <li>You'll remain logged in after changing your password</li>
                  </ul>
                </div>
              </IonCardContent>
            </IonCard>
          </div>
        </IonContent>
      </IonModal>

      {/* Password Success Toast */}
      <IonToast
        isOpen={passwordSuccess}
        onDidDismiss={() => setPasswordSuccess(false)}
        message="Password updated successfully!"
        duration={3000}
        position="bottom"
        color="success"
      />
    </>
  )
}

export default UserMenu 